/**
 * Formulaires du socle : React Hook Form branché sur les schémas Zod partagés avec l'API (`packages/shared`).
 * Un écran ne gère plus lui-même valeurs, messages d'erreur, brouillon ni envoi :
 *
 *   const f = useFormulaire({
 *     schema: schemaContactNouveau,                       // mêmes règles que l'API
 *     valeurs: initial,                                   // valeurs du formulaire (chaînes vides pour « non renseigné »)
 *     preparer: ({ id, ...v }) => v,                      // facultatif : forme envoyée (champs propres au mode…)
 *     envoyer: (corps) => api.post<Contact>('/v1/…', corps), // corps validé et nettoyé par le schéma
 *     apresEnvoi: (enr) => onFin(enr),
 *   });
 *   <Formulaire formulaire={f} titre={…}>
 *     <TextField label={t('…')} {...f.champ('nom')} />
 *     <Toggle label={t('…')} {...f.interrupteur('actif')} />
 *     <ActionsFormulaire formulaire={f} onAnnuler={() => onFin()} />
 *   </Formulaire>
 *
 * Comportement commun à tous les formulaires : validation à l'envoi avec le schéma partagé, puis à chaque frappe sur
 * les champs signalés ; messages traduits (`formulaires.erreurs.*`, 6 langues) sous chaque champ ; bandeau
 * « Corrigez les champs signalés » et focus sur le premier champ en erreur ; erreurs de l'API placées sur leurs champs
 * (sinon message `erreurs.<code>`) ; page marquée « brouillon non enregistré » dès qu'une valeur change (useBrouillon) ;
 * bouton d'envoi désactivé pendant l'envoi ; après succès, plus de brouillon.
 */
import React from 'react';
import { useForm, type DefaultValues, type FieldErrors, type FieldValues, type Path, type PathValue, type Resolver } from 'react-hook-form';
import {
  PREFIXE_MESSAGE_SAISIE, analyserSaisie, cleExiste, cleLibre,
  type ProblemeSaisie, type SchemaSaisie, type SortieSaisie,
} from '@agora/shared';
import { Button, Icon, type IconName } from '../../design';
import { useT, type Traduire } from '../i18n';
import { ErreurApi } from '../api/client';
import { useBrouillon } from '../pages/espace';

export interface OptionsFormulaire<V extends FieldValues, S extends SchemaSaisie, R> {
  /** Schéma Zod partagé avec l'API (création ou modification), éventuellement complété (`.superRefine(regle…)`). */
  schema: S;
  /** Valeurs initiales du formulaire (référence du brouillon). */
  valeurs: V;
  /**
   * Passage des valeurs du formulaire à l'entrée du schéma (= corps de l'API) : retirer un champ affiché seulement,
   * garder les champs propres à la création ou à la modification… Sans `preparer`, les valeurs telles quelles. Dans
   * tous les cas, les champs `undefined` sont retirés (schémas stricts).
   */
  preparer?: (valeurs: V) => Record<string, unknown>;
  /** Envoi à l'API du corps validé et nettoyé par le schéma ; une ErreurApi est affichée dans le formulaire. */
  envoyer: (corps: SortieSaisie<S>) => Promise<R>;
  /** Après un envoi réussi (mise à jour des caches, fermeture du formulaire…). */
  apresEnvoi?: (resultat: R) => void;
  /**
   * Message propre à un champ pour une valeur au format refusé (ex. pays : t('formulaires.erreurs.pays')), déjà
   * traduit. Les messages « obligatoire », « trop long »… restent les messages communs.
   */
  messages?: Partial<Record<Path<V>, string>>;
}

/** Codes dont le message peut être remplacé par le message propre au champ (`messages`). */
const FORMAT = new Set<ProblemeSaisie['code']>(['format', 'email', 'url', 'valeur', 'invalide']);

/** Message traduit d'un problème de saisie. */
export function messageSaisie(p: ProblemeSaisie, t: Traduire, propre?: string): string {
  if (propre && FORMAT.has(p.code)) return propre;
  if (p.code === 'personnalise') {
    return p.cle && cleExiste(p.cle) ? t(cleLibre('formulaires.erreurs', p.cle.slice(PREFIXE_MESSAGE_SAISIE.length))) : t('formulaires.erreurs.invalide');
  }
  return t(cleLibre('formulaires.erreurs', p.code), { min: p.min ?? 0, max: p.max ?? 0 });
}

/** Retire les champs `undefined` (un schéma strict refuse une clé présente, même vide). */
function sansIndefinis(v: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(v).filter(([, x]) => x !== undefined));
}

/** Noms des champs en erreur dans l'arbre de React Hook Form ({ adresse: { ville } } → `adresse.ville`). */
function champsEnErreur(arbre: unknown, prefixe = ''): string[] {
  if (!arbre || typeof arbre !== 'object') return [];
  if ('type' in arbre && 'message' in arbre) return [prefixe || '_'];
  return Object.entries(arbre).flatMap(([k, v]) => (k === 'ref' ? [] : champsEnErreur(v, prefixe ? `${prefixe}.${k}` : k)));
}

/** Place une erreur dans l'arbre des erreurs de React Hook Form (`adresse.ville` → { adresse: { ville } }). */
function placer(arbre: Record<string, unknown>, chemin: string, erreur: { type: string; message: string }) {
  const segments = chemin.split('.');
  let noeud = arbre;
  segments.slice(0, -1).forEach((s) => { noeud = (noeud[s] ??= {}) as Record<string, unknown>; });
  noeud[segments[segments.length - 1]!] = erreur;
}

export function useFormulaire<V extends FieldValues, S extends SchemaSaisie, R = unknown>(o: OptionsFormulaire<V, S, R>) {
  const { t } = useT();
  const opts = React.useRef(o);
  opts.current = o;
  const formulaire = React.useRef<HTMLFormElement | null>(null);
  const [erreur, setErreur] = React.useState<string | undefined>();

  // Résolveur : le schéma partagé valide les valeurs préparées ; chaque problème devient un message traduit.
  const resolver = React.useMemo<Resolver<V, unknown, SortieSaisie<S>>>(() => async (valeurs) => {
    const { schema, preparer, messages } = opts.current;
    const r = analyserSaisie(schema, sansIndefinis(preparer ? preparer(valeurs) : valeurs));
    if (r.ok) return { values: r.donnees, errors: {} };
    const erreurs: Record<string, unknown> = {};
    for (const p of r.problemes) {
      placer(erreurs, p.champ, { type: p.code, message: messageSaisie(p, t, (messages as Record<string, string | undefined> | undefined)?.[p.champ]) });
    }
    return { values: {}, errors: erreurs as FieldErrors<V> };
  }, [t]);

  const rhf = useForm<V, unknown, SortieSaisie<S>>({
    defaultValues: o.valeurs as DefaultValues<V>, resolver, mode: 'onSubmit', reValidateMode: 'onChange', shouldFocusError: false,
  });
  const { formState, watch, setValue, getFieldState, handleSubmit, setError, reset, getValues } = rhf;
  useBrouillon(formState.isDirty);

  // Champs affichés au dernier rendu (liés par champ, interrupteur, choix ou erreurDe) : une règle non respectée sur un
  // champ absent du formulaire ne doit pas laisser l'utilisateur devant « Corrigez les champs signalés » sans repère.
  const affiches = React.useRef(new Set<string>());
  affiches.current = new Set();
  const lier = (nom: string) => { affiches.current.add(nom); };

  // Premier champ en erreur (classe `ag-field-error` des champs du design, ou aria-invalid) : focus et défilement.
  const focusPremiereErreur = () => window.requestAnimationFrame(() => {
    const zone = formulaire.current?.querySelector<HTMLElement>('.ag-field-error, [aria-invalid="true"]');
    const cible = zone?.matches('input, select, textarea, button')
      ? zone
      : zone?.querySelector<HTMLElement>('input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])');
    cible?.focus();
  });

  /** Bandeau et focus après un refus ; champ en erreur non affiché : message général, et erreur pour le développeur. */
  const signalerRefus = (champs: string[]) => {
    const caches = champs.filter((c) => !affiches.current.has(c));
    if (caches.length && import.meta.env.DEV) {
      console.error(`[AGORA] useFormulaire : règle non respectée sur un champ non affiché (${caches.join(', ')}) – vérifier le schéma, les valeurs initiales ou preparer().`);
    }
    setErreur(t(caches.length ? 'erreurs.validation' : 'formulaires.corriger'));
    focusPremiereErreur();
  };

  const soumettre = handleSubmit(async (corps) => {
    setErreur(undefined);
    try {
      const resultat = await opts.current.envoyer(corps);
      reset(getValues()); // valeurs enregistrées = nouvelle référence : plus de brouillon
      opts.current.apresEnvoi?.(resultat);
    } catch (e) {
      const err = e instanceof ErreurApi ? e : new ErreurApi('interne', 500);
      const champs = Object.entries(err.champs ?? {}).filter(([champ]) => champ !== '_');
      const libelleCode = t(cleLibre('erreurs', err.code));
      if (champs.length) {
        // Règle vérifiée par l'API seulement (400 validation), ou refus rattaché à un champ (ex. 409 depot_modifie sur
        // piecesJointes.<i>) : message du dictionnaire si l'API en donne la clé, sinon message du code.
        const noms = champs.map(([champ, message]) => {
          const nom = champ.split('.').filter((s) => !/^\d+$/.test(s)).join('.');
          const traduit = message.startsWith(PREFIXE_MESSAGE_SAISIE) && cleExiste(message)
            ? t(cleLibre('formulaires.erreurs', message.slice(PREFIXE_MESSAGE_SAISIE.length)))
            : libelleCode;
          setError(nom as Path<V>, { type: 'serveur', message: traduit });
          return nom;
        });
        signalerRefus(noms);
        // Refus autre qu'une validation : le bandeau dit la cause (le message du champ dit quoi faire).
        if (err.code !== 'validation') setErreur(libelleCode);
      } else {
        setErreur(libelleCode);
      }
    }
  }, (erreurs) => signalerRefus(champsEnErreur(erreurs)));

  const modifier = <N extends Path<V>>(nom: N, valeur: PathValue<V, N>) =>
    setValue(nom, valeur, { shouldDirty: true, shouldValidate: formState.isSubmitted });

  return {
    /**
     * Props d'un champ du design à valeur (TextField, TextArea, SelectField, SegmentedControl…) :
     * `{ name, value, onChange, error }` – error = message traduit, affiché sous le champ.
     */
    champ: <N extends Path<V>>(nom: N) => (lier(nom), {
      name: nom,
      value: watch(nom),
      onChange: (valeur: unknown) => modifier(nom, valeur as PathValue<V, N>),
      error: getFieldState(nom, formState).error?.message,
    }),
    /** Props d'un interrupteur (Toggle) lié à un champ booléen : `{ checked, onChange }`. */
    interrupteur: <N extends Path<V>>(nom: N) => (lier(nom), {
      checked: !!watch(nom),
      onChange: (on: boolean) => modifier(nom, on as PathValue<V, N>),
    }),
    /**
     * Props d'un interrupteur qui ajoute / retire `valeur` d'un champ liste (rôles, groupes…) ; `ordre` = liste de
     * référence pour garder un ordre stable.
     */
    choix: <N extends Path<V>>(nom: N, valeur: string, ordre?: readonly string[]) => {
      lier(nom);
      const liste = (watch(nom) as string[] | undefined) ?? [];
      return {
        checked: liste.includes(valeur),
        onChange: (on: boolean) => {
          const suivante = on ? [...liste, valeur] : liste.filter((x) => x !== valeur);
          modifier(nom, (ordre ? ordre.filter((x) => suivante.includes(x)) : suivante) as PathValue<V, N>);
        },
      };
    },
    /** Valeur courante d'un champ (affichage conditionnel). */
    valeur: <N extends Path<V>>(nom: N): PathValue<V, N> => watch(nom),
    /** Modifier un champ par code (ex. mise en majuscules à la saisie). */
    modifier,
    /** Message d'erreur traduit d'un champ (groupe d'interrupteurs, champ composé). */
    erreurDe: (nom: Path<V>) => (lier(nom), getFieldState(nom, formState).error?.message),
    /** À étaler sur l'élément `<form>` (ou utiliser `<Formulaire>`). */
    proprietes: {
      ref: formulaire,
      noValidate: true,
      onSubmit: (e: React.FormEvent) => { void soumettre(e); },
    },
    /** Message général (champs à corriger, erreur de l'API), déjà traduit. */
    erreur,
    envoiEnCours: formState.isSubmitting,
    modifie: formState.isDirty,
    /** Accès direct à React Hook Form (cas particuliers : listes de lignes, champs dépendants). */
    rhf,
  };
}

/** État et liaisons d'un formulaire (retour de useFormulaire). */
export type EtatFormulaire = ReturnType<typeof useFormulaire>;

export interface FormulaireProps {
  formulaire: Pick<EtatFormulaire, 'proprietes' | 'erreur'>;
  /** Titre affiché en tête (h2) et nom accessible du formulaire. */
  titre?: string;
  className?: string;
  children?: React.ReactNode;
}

/** Cadre commun d'un formulaire : carte du design, titre, bandeau d'erreur général, champs. */
export function Formulaire({ formulaire, titre, className = 'ag-glass-card agx-carte agx-formulaire', children }: FormulaireProps) {
  return (
    <form className={className} aria-label={titre} {...formulaire.proprietes}>
      {titre ? <h2 className="agx-titre-carte">{titre}</h2> : null}
      {formulaire.erreur ? <div className="agx-message" role="alert"><Icon name="bell" size={16} /><span>{formulaire.erreur}</span></div> : null}
      {children}
    </form>
  );
}

export interface ActionsFormulaireProps {
  formulaire: Pick<EtatFormulaire, 'envoiEnCours'>;
  /** Bouton « Annuler » (absent sans onAnnuler). */
  onAnnuler?: () => void;
  /** Libellés traduits (défaut : « Enregistrer », « Annuler »). */
  envoyerLabel?: string;
  annulerLabel?: string;
  /** Icône du bouton principal, accordée à son libellé (défaut : « check » ; « send » pour « Envoyer »…). */
  icone?: IconName;
}

/** Boutons du pied de formulaire : Annuler (discret) puis Enregistrer (principal, désactivé pendant l'envoi). */
export function ActionsFormulaire({ formulaire, onAnnuler, envoyerLabel, annulerLabel, icone = 'check' }: ActionsFormulaireProps) {
  const { t } = useT();
  return (
    <div className="agx-actions">
      {onAnnuler ? <Button variant="quiet" onClick={onAnnuler}>{annulerLabel ?? t('commun.annuler')}</Button> : null}
      <Button variant="primary" type="submit" icon={icone} disabled={formulaire.envoiEnCours}>{envoyerLabel ?? t('commun.enregistrer')}</Button>
    </div>
  );
}
